import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  Box,
  Link,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import api from '../../api/config';
import { IMPORT_BUSINESSES } from '../../api/endpoints';
import { toast } from 'react-toastify';

const ACCEPTED_FILE_TYPES =
  '.csv,.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv';

const DEFAULT_SAMPLE_FILE_NAME = 'import-business-sample.xlsx';

function getFilenameFromContentDisposition(header) {
  if (!header) return null;
  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1].replace(/["']/g, ''));
  }
  const filenameMatch = header.match(/filename="?([^";]+)"?/i);
  return filenameMatch?.[1]?.trim() || null;
}

function mergeSelectedFiles(previous, incoming) {
  const next = [...previous];
  const seen = new Set(
    previous.map((file) => `${file.name}::${file.size}::${file.lastModified}`)
  );

  Array.from(incoming || []).forEach((file) => {
    const key = `${file.name}::${file.size}::${file.lastModified}`;
    if (seen.has(key)) return;
    seen.add(key);
    next.push(file);
  });

  return next;
}

const ImportBusinessDialog = ({ open, onClose }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sampleDownloading, setSampleDownloading] = useState(false);

  const handleFileChange = (event) => {
    const selected = event.target.files;
    if (!selected?.length) return;
    setFiles((prev) => mergeSelectedFiles(prev, selected));
    // Allow selecting the same file again after clear/remove
    event.target.value = '';
  };

  const handleRemoveFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleClose = () => {
    if (loading) return;
    setFiles([]);
    onClose(false);
  };

  const handleDownloadSampleFile = async () => {
    setSampleDownloading(true);
    try {
      const response = await api.get(IMPORT_BUSINESSES.SAMPLE_FILE, { responseType: 'blob' });
      const blob = response.data;
      const fileName =
        getFilenameFromContentDisposition(response.headers?.['content-disposition']) ||
        DEFAULT_SAMPLE_FILE_NAME;
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Sample file downloaded');
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Failed to download sample file');
    } finally {
      setSampleDownloading(false);
    }
  };

  const handleImport = async () => {
    if (!files.length) {
      toast.error('Please select at least one file');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files[]', file);
    });

    try {
      const { data } = await api.post(IMPORT_BUSINESSES.IMPORT, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(data?.message || 'Data imported successfully');
      setFiles([]);
      onClose(true);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Failed to import data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Import Data</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 1 }}>
          Upload one or more Excel files containing business data. Supported formats: .xls, .xlsx
        </DialogContentText>
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={handleDownloadSampleFile}
          disabled={loading || sampleDownloading}
          sx={{
            mb: 3,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            textAlign: 'left',
          }}
        >
          {sampleDownloading ? (
            <CircularProgress size={16} />
          ) : (
            <DownloadIcon sx={{ fontSize: 18 }} />
          )}
          Download the sample file
        </Link>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <input
            accept={ACCEPTED_FILE_TYPES}
            style={{ display: 'none' }}
            id="import-business-file-input"
            type="file"
            multiple
            onChange={handleFileChange}
            disabled={loading}
          />
          <label htmlFor="import-business-file-input">
            <Button
              variant="outlined"
              component="span"
              fullWidth
              startIcon={<CloudUploadIcon />}
              disabled={loading}
            >
              {files.length > 0
                ? `Add more files (${files.length} selected)`
                : 'Choose Excel files'}
            </Button>
          </label>

          {files.length > 0 && (
            <>
              <List dense sx={{ bgcolor: 'grey.50', borderRadius: 1, py: 0.5 }}>
                {files.map((file, index) => (
                  <ListItem
                    key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        aria-label={`Remove ${file.name}`}
                        onClick={() => handleRemoveFile(index)}
                        disabled={loading}
                        size="small"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={file.name}
                      primaryTypographyProps={{ noWrap: true, title: file.name }}
                    />
                  </ListItem>
                ))}
              </List>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary">
                  {files.length} file{files.length === 1 ? '' : 's'} selected
                </Typography>
                <Button
                  size="small"
                  onClick={() => setFiles([])}
                  color="error"
                  disabled={loading}
                >
                  Clear all
                </Button>
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleImport}
          variant="contained"
          disabled={!files.length || loading}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {loading ? 'Importing...' : 'Import'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportBusinessDialog;
